'use client';

import { ReactNode, useState } from 'react';
import packageComponentHelper from '@helpers/packageComponentHelper';
import CmsComponent from '@organisms/CmsComponent/CmsComponent';
import { useTranslations } from 'next-intl';
import useSnackBar from '@hooks/useSnackbar';
import type { AxfoodBasicProductViewModel, AxfoodProductDetailsViewModel } from '@occ/api-client';
import ProductContainer from '@molecules/ProductContainer/ProductContainer';
import Cart from '@icons/cart.svg';
import { useAmountOfItemsPerSlide } from '@hooks/useAmountOfItemsPerSlide';
import useCmsComponents from '@hooks/useCmsComponents';
import { trackCloseModal, trackOpenModal } from '@helpers/analyticsHelpers/trackBuyAllProductsComponent';
import { StyledButton, StyledHeading } from './BuyAllProductsComponent.styles';
import type { ListAdaptedComponents, ComponentWsDTO } from '@occ/api-client';
import type { BuyAllProductsWsDTO } from '@api/interfaces/componentApi';
import useBuyAllProducts from '@organisms/BuyAllProductsComponent/useBuyAllProducts';
import TrackedComponent from '@organisms/TrackedComponent/TrackedComponent';
import Product from '@molecules/Product/Product';
import ProductBeamSkeleton from '@molecules/ProductBeam/ProductBeam.skeleton';
import useProductImpressionTracking from '@hooks/useProductImpressionsTracking';
import Carousel from '@molecules/Carousel/Carousel';
import paths from '@constants/paths';
import SkipToContent from '@molecules/SkipToContent/SkipToContent';
import useMediaQueryKey from '@hooks/useMediaQueryKey';
import { usePathname } from 'next/navigation';
import BuyAllProductsModal from './BuyAllProductsModal';

export interface GridPackagesComponentWsDTO extends ComponentWsDTO {
  displayType: string;
  gridPackages: string;
}

interface Props {
  data: GridPackagesComponentWsDTO;
}

const BuyAllProductsComponent = ({ data }: Props) => {
  const [showBuyAllProductsModal, setShowBuyAllProductsModal] = useState(false);
  const { data: componentContent, error } = useBuyAllProducts(data?.uid);
  const { data: cmsComponents } = useCmsComponents(JSON.stringify(data?.gridPackages?.replace(/[[\]"]+/g, '')));

  const t = useTranslations('buyAllProductsComponent');
  const tCommon = useTranslations('common');
  const pathname = usePathname();
  const isHomePage = pathname === paths.HOME;
  const { setSnack } = useSnackBar();
  const amountOfItemsPerSlide = useAmountOfItemsPerSlide();

  const packageHelper = packageComponentHelper();
  const getCurrentMediaQueryKey = useMediaQueryKey();

  const productListWithPositions = (
    amountOfItemsPerSlide: number,
    cmsComponents?: ListAdaptedComponents,
    componentContent?: BuyAllProductsWsDTO
  ) => {
    if (cmsComponents?.component && componentContent?.products) {
      return packageHelper.positionPackagesInList(
        componentContent.products,
        cmsComponents.component as ImageGridComponentType[],
        getCurrentMediaQueryKey(),
        amountOfItemsPerSlide
      );
    }
    return componentContent?.products;
  };

  const productsOrImages = productListWithPositions(amountOfItemsPerSlide, cmsComponents, componentContent);

  const { onProductShown } = useProductImpressionTracking('buy-all-products-component');
  const targetId = `${data.uid}-beam-target`;

  const getProductElements = (items: (AxfoodBasicProductViewModel | ImageGridComponentType)[]) => {
    const elementsArr: Array<ReactNode> = [];
    items?.forEach((child, i) => {
      if (Object.prototype.hasOwnProperty.call(child, 'typeCode')) {
        elementsArr.push(<CmsComponent component={{ ...child, gridCols: amountOfItemsPerSlide }} />);
      } else {
        elementsArr.push(
          <TrackedComponent
            index={i}
            product={child as AxfoodProductDetailsViewModel}
            key={`buy-all-${child.code}`}
            onInView={() => onProductShown(child as AxfoodProductDetailsViewModel, i)}
          >
            <Product
              product={child as AxfoodProductDetailsViewModel | AxfoodBasicProductViewModel}
              variant="multisearch"
              category={`buy-all-products-component - ${title}`}
              disableMixMatchButton
            />
          </TrackedComponent>
        );
      }
    });
    return elementsArr;
  };

  const handleModalClose = (purchased?: boolean) => {
    setShowBuyAllProductsModal(false);
    if (purchased) {
      setSnack({ text: t('snackMessage'), icon: 'valid' });
    } else {
      trackCloseModal();
    }
  };

  if (!componentContent || !productsOrImages) {
    if (!error) return <ProductBeamSkeleton type="buyAll" />;
    return null;
  }

  const { title, products } = componentContent;

  return (
    <div id={targetId}>
      {isHomePage && (
        <SkipToContent
          buttonText={tCommon('navigation.skipProductBeam')}
          targetElementSelector={`#${targetId}`}
          focusLast
        />
      )}
      <>
        {data.displayType === 'SINGLE_ROW' && (
          <Carousel
            elements={getProductElements(productsOrImages)}
            elementsPerSlide={amountOfItemsPerSlide}
            variant="buyAllProducts"
            title={title}
            key={title}
          />
        )}

        {data.displayType === 'MULTIPLE_ROWS' && (
          <>
            <StyledHeading variant="h2" size="small">
              {title}
            </StyledHeading>

            <ProductContainer
              title={title}
              products={productsOrImages}
              productType="online"
              offline={false}
              numberOfGridCols={amountOfItemsPerSlide}
              disableMixMatchButton
            />
          </>
        )}
      </>
      <StyledButton
        onClick={() => {
          setShowBuyAllProductsModal(true);
          trackOpenModal();
        }}
        icon={Cart}
      >
        {t('buyAllProducts', { amount: products.length || 0 })}
      </StyledButton>

      <BuyAllProductsModal
        isOpen={showBuyAllProductsModal}
        onClose={handleModalClose}
        title={title}
        products={products}
      />
    </div>
  );
};

export default BuyAllProductsComponent;
